// Voice client. Speaks the coach's replies using the backend's premium TTS (natural, per-coach
// voices) when available, and falls back to the browser's on-device speech synthesis otherwise —
// so voice always works, and upgrades automatically the moment a premium key is configured.
import { API_BASE } from './chat'

let premium: boolean | null = null // cached /status result
let current: HTMLAudioElement | null = null

export async function voiceAvailable(): Promise<boolean> {
  if (premium !== null) return premium
  try {
    const r = await fetch(`${API_BASE}/v1/voice/status`)
    const d = await r.json()
    premium = !!d.available
  } catch {
    premium = false
  }
  return premium
}

// Per-coach delivery for the on-device fallback (rate/pitch shape the personality).
const FALLBACK_TUNING: Record<string, { rate: number; pitch: number; female: boolean }> = {
  dorothy: { rate: 0.92, pitch: 1.0, female: true },
  katy: { rate: 1.12, pitch: 1.22, female: true },
  marcus: { rate: 0.96, pitch: 0.85, female: false },
  sana: { rate: 1.0, pitch: 1.06, female: true },
}

function pickVoice(female: boolean): SpeechSynthesisVoice | undefined {
  const voices = window.speechSynthesis?.getVoices?.() || []
  if (!voices.length) return undefined
  const en = voices.filter((v) => v.lang?.startsWith('en'))
  const pool = en.length ? en : voices
  const byName = pool.find((v) =>
    female ? /female|samantha|victoria|karen|moira|tessa|zira|serena/i.test(v.name)
           : /male|daniel|alex|fred|david|rishi|arthur/i.test(v.name))
  return byName || pool[0]
}

export function stopSpeaking() {
  try { current?.pause(); current = null } catch { /* */ }
  try { window.speechSynthesis?.cancel() } catch { /* */ }
}

/** Speak `text` in the coach's voice. Resolves when playback finishes (or fails silently). */
export async function speak(text: string, coach: string, opts?: { onStart?: () => void; onEnd?: () => void }): Promise<void> {
  stopSpeaking()
  const clean = text.replace(/[*_`#>]/g, '').replace(/\s+/g, ' ').trim()
  if (!clean) return

  if (await voiceAvailable()) {
    try {
      const r = await fetch(`${API_BASE}/v1/voice/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: clean, coach }),
      })
      if (r.ok) {
        const url = URL.createObjectURL(await r.blob())
        const audio = new Audio(url)
        current = audio
        opts?.onStart?.()
        await new Promise<void>((resolve) => {
          audio.onended = audio.onerror = () => { URL.revokeObjectURL(url); resolve() }
          audio.play().catch(() => resolve())
        })
        current = null
        opts?.onEnd?.()
        return
      }
    } catch { /* fall through to on-device */ }
  }

  // On-device fallback.
  const synth = window.speechSynthesis
  if (!synth) return
  const t = FALLBACK_TUNING[coach] || { rate: 1, pitch: 1, female: true }
  const u = new SpeechSynthesisUtterance(clean)
  u.rate = t.rate; u.pitch = t.pitch
  const v = pickVoice(t.female); if (v) u.voice = v
  opts?.onStart?.()
  await new Promise<void>((resolve) => {
    u.onend = u.onerror = () => resolve()
    synth.speak(u)
  })
  opts?.onEnd?.()
}
